import { useEffect, useState } from "react";
import { useClerk } from "@clerk/react";
import { Link } from "react-router-dom";
import { KeyRound, ShieldCheck, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, timeAgo } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const CodeInput = ({ value, onChange, testid, placeholder = "6-digit code" }) => (
  <Input value={value} onChange={(e) => onChange(e.target.value)} inputMode="numeric" autoComplete="one-time-code" placeholder={placeholder} className="max-w-[200px] text-center text-lg tracking-widest" data-testid={testid} />
);

export const TwoStepSignIn = () => {
  const [st, setSt] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState(null);
  const [devices, setDevices] = useState([]);
  const load = () => { api.get("/security/mfa").then((r) => setSt(r.data)); api.get("/security/devices").then((r) => setDevices(r.data)); };
  useEffect(load, []);
  const begin = async () => { try { setSetup((await api.post("/security/mfa/setup")).data); setCode(""); } catch (e) { toast.error(errMsg(e)); } };
  const enable = async () => { try { setCodes((await api.post("/security/mfa/enable", { code })).data.backup_codes); setSetup(null); setCode(""); load(); toast.success("Two-step sign-in is on"); } catch (e) { toast.error(errMsg(e)); } };
  const disable = async () => { try { await api.post("/security/mfa/disable", { code }); setCode(""); load(); toast.success("Two-step sign-in is off"); } catch (e) { toast.error(errMsg(e)); } };
  if (!st) return null;
  return (
    <section className="surface space-y-4 p-5 sm:p-6" data-testid="two-step-section">
      <div className="flex items-start gap-3">
        <ShieldCheck size={20} className="mt-1 shrink-0 text-[hsl(var(--facts))]" />
        <div className="flex-1"><h2 className="text-xl">Two-step sign-in</h2>
          <p className="text-xs text-slate-500">Optional. After your password, Middle also asks for a code from an authenticator app on your phone (such as Google Authenticator, Microsoft Authenticator or 1Password).</p>
          <p className="mt-1 text-xs font-semibold" data-testid="two-step-state">{st.enabled ? `On · ${st.backup_left} backup codes left` : "Off"}</p></div>
      </div>
      {codes && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm" data-testid="two-step-backup-codes">
          <p className="font-semibold text-amber-900">Save these backup codes somewhere safe. Each works once if you lose your phone. They won't be shown again.</p>
          <ul className="mt-2 grid grid-cols-2 gap-1 font-mono text-sm">{codes.map((c) => <li key={c}>{c}</li>)}</ul>
          <Button size="sm" variant="outline" className="mt-2 rounded-full" onClick={() => setCodes(null)} data-testid="two-step-codes-saved">I've saved them</Button>
        </div>
      )}
      {!st.enabled && !setup && <Button className="rounded-full" onClick={begin} data-testid="two-step-start"><Smartphone size={15} className="mr-1.5" />Turn on two-step sign-in</Button>}
      {setup && (
        <div className="space-y-3" data-testid="two-step-setup">
          <p className="text-sm">1. Scan this with your authenticator app.</p>
          <img src={setup.qr} alt="QR code for your authenticator app" className="h-44 w-44 rounded-lg border border-border" data-testid="two-step-qr" />
          <p className="text-xs text-slate-500">Can't scan? Enter this key: <code className="break-all font-mono" data-testid="two-step-secret">{setup.secret}</code></p>
          <p className="text-sm">2. Enter the 6-digit code it shows.</p>
          <div className="flex flex-wrap gap-2"><CodeInput value={code} onChange={setCode} testid="two-step-code" /><Button className="rounded-full" onClick={enable} data-testid="two-step-enable">Turn on</Button>
            <Button variant="ghost" onClick={() => setSetup(null)}>Cancel</Button></div>
        </div>
      )}
      {st.enabled && (
        <div className="flex flex-wrap items-center gap-2"><CodeInput value={code} onChange={setCode} testid="two-step-disable-code" placeholder="Code to turn off" />
          <Button variant="outline" className="rounded-full" onClick={disable} data-testid="two-step-disable">Turn off</Button></div>
      )}
      {devices.length > 0 && (
        <div className="border-t border-border pt-3" data-testid="security-devices">
          <p className="mb-1 text-xs font-semibold text-slate-500">Recent sign-ins. We email you when a new device signs in.</p>
          <ul className="space-y-0.5 text-xs text-slate-600">{devices.slice(0, 6).map((d, i) => <li key={i}>{d.label} · {timeAgo(d.first_seen)}</li>)}</ul>
        </div>
      )}
    </section>
  );
};

export const MfaGate = () => {
  const [code, setCode] = useState("");
  const { signOut } = useClerk();
  const verify = async () => { try { await api.post("/security/mfa/verify", { code }); window.location.reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FAF8F5] p-6" data-testid="mfa-gate">
      <div className="surface w-full max-w-sm space-y-4 p-6 text-center">
        <KeyRound size={28} className="mx-auto text-[hsl(var(--facts))]" />
        <h1 className="font-serif text-2xl">Two-step sign-in</h1>
        <p className="text-sm text-slate-600">Enter the 6-digit code from your authenticator app, or one of your backup codes.</p>
        <form className="flex flex-col items-center gap-3" onSubmit={(e) => { e.preventDefault(); verify(); }}>
          <CodeInput value={code} onChange={setCode} testid="mfa-gate-code" placeholder="Code" />
          <Button type="submit" className="w-full rounded-full" data-testid="mfa-gate-submit">Continue</Button>
        </form>
        <button className="text-xs text-slate-500 underline" onClick={() => signOut()} data-testid="mfa-gate-signout">Sign out</button>
      </div>
    </div>
  );
};

export const WasntMe = () => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.post("/security/wasnt-me", { t: new URLSearchParams(window.location.search).get("t") || "" }); } catch { /* same outcome either way */ }
    window.location.assign("/signed-out-everywhere");
  };
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FAF8F5] p-6" data-testid="wasnt-me">
      <div className="surface max-w-md space-y-3 p-6 text-center">
        <ShieldCheck size={28} className="mx-auto text-rose-600" />
        <h1 className="font-serif text-2xl">Wasn't you?</h1>
        <p className="text-sm text-slate-600">Tap below and we'll sign your Middle account out on every device straight away, including the one you didn't recognise.</p>
        <Button className="rounded-full bg-rose-600 hover:bg-rose-700" disabled={busy} onClick={go} data-testid="wasnt-me-confirm">Sign out everywhere</Button>
      </div>
    </div>
  );
};

export const SignedOutEverywhere = () => {
  const { signOut } = useClerk();
  useEffect(() => { signOut().catch(() => {}); }, []); // eslint-disable-line
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FAF8F5] p-6" data-testid="signed-out-everywhere">
      <div className="surface max-w-md space-y-3 p-6 text-center">
        <ShieldCheck size={28} className="mx-auto text-[hsl(var(--facts))]" />
        <h1 className="font-serif text-2xl">You're signed out everywhere</h1>
        <p className="text-sm text-slate-600">If that link was valid, every device signed into your Middle account has now been signed out. Sign in again, then change your password and consider turning on two-step sign-in in Settings.</p>
        <Link to="/sign-in" className="inline-block rounded-full bg-slate-900 px-6 py-2.5 text-sm font-medium text-white" data-testid="signed-out-signin">Sign in</Link>
      </div>
    </div>
  );
};
